/**
 * platform/web/src/ui/state/savedata.ts
 *
 * The per-title save-data model + persistence seam (Roadmap §4 / persistence).
 *
 * The C core keeps a SEPARATE writable save-data filesystem (`ctx->savefs`,
 * opened from fsp-srv OpenSaveDataFileSystem cmd 51) so a game's persistent data
 * never aliases the SD card. On the web that save tree has to survive a page
 * reload, which means persisting it to durable browser storage. The durable
 * store of record is the Origin Private File System (OPFS) — `capabilities.ts`
 * already feature-detects it (`opfs`).
 *
 * This module is the headlessly-testable HALF of that:
 *   - a small, PURE manifest model — which titles have a save blob, plus its
 *     byte size and last-modified time — keyed by the SAME stable {@link Title}
 *     `id` the library uses, so a save is unambiguously tied to one title;
 *   - (DE)SERIALISATION of that manifest that is TOTAL (garbage / missing /
 *     wrong-typed / unknown -> a clean manifest, never a throw), exactly like the
 *     settings model, so a corrupt store can never crash the app;
 *   - an injectable {@link SaveDataStorage} seam for the blobs themselves. OPFS
 *     is async, so the seam is Promise-based. An in-memory fake
 *     ({@link createMemorySaveDataStorage}) is the default and the one tests
 *     drive, so all of this logic is verifiable under Node with no browser; a
 *     real OPFS adapter ({@link createOpfsSaveDataStorage}, browser-only,
 *     build-verified) slots in behind the same interface.
 *
 * §1.6: a save blob is the game's OWN persistent data produced at runtime — it
 * is NOT game content, a ROM, a key, or anything decrypted. We never read, hash,
 * or interpret the bytes here; we just move and account for an opaque buffer.
 */

/** One title's save-data accounting (NOT the bytes — those live in storage). */
export interface SaveDataEntry {
  /** The owning {@link Title} id (stable, content-derived; see library.titleId). */
  readonly titleId: string;
  /** Size in bytes of the stored save blob. */
  readonly size: number;
  /** Epoch ms the save was last written. */
  readonly updatedAt: number;
}

/** The manifest: every title that has a persisted save, newest-written first is
 *  the caller's concern; stored here in stable id order for deterministic output. */
export interface SaveDataManifest {
  readonly entries: readonly SaveDataEntry[];
}

/** The empty manifest (nothing saved yet). */
export function emptyManifest(): SaveDataManifest {
  return { entries: [] };
}

/** Look up one title's save accounting, or null if it has no save. */
export function getEntry(manifest: SaveDataManifest, titleId: string): SaveDataEntry | null {
  return manifest.entries.find((e) => e.titleId === titleId) ?? null;
}

/** True iff `titleId` has a persisted save recorded in the manifest. */
export function hasSave(manifest: SaveDataManifest, titleId: string): boolean {
  return manifest.entries.some((e) => e.titleId === titleId);
}

/**
 * Record (or refresh) a title's save accounting, returning a fresh manifest. An
 * existing entry for the same title is replaced IN PLACE (preserving id order);
 * a new entry is appended. Never mutates the input. The blob bytes are the
 * storage layer's job — this only tracks size + time.
 *
 * @param now epoch ms to stamp the write with (injectable for tests).
 */
export function recordSave(
  manifest: SaveDataManifest,
  titleId: string,
  size: number,
  now = Date.now(),
): SaveDataManifest {
  const entry: SaveDataEntry = { titleId, size, updatedAt: now };
  const index = manifest.entries.findIndex((e) => e.titleId === titleId);
  if (index >= 0) {
    const entries = manifest.entries.slice();
    entries[index] = entry;
    return { entries };
  }
  return { entries: [...manifest.entries, entry] };
}

/**
 * Forget a title's save accounting, returning a fresh manifest. Removing a title
 * with no entry returns the same reference (no-op). Does NOT itself delete the
 * blob — pair with {@link SaveDataStorage.delete} (the wiring does both).
 */
export function forgetSave(manifest: SaveDataManifest, titleId: string): SaveDataManifest {
  if (!manifest.entries.some((e) => e.titleId === titleId)) return manifest;
  return { entries: manifest.entries.filter((e) => e.titleId !== titleId) };
}

/** Total bytes accounted for across all saved titles. */
export function totalBytes(manifest: SaveDataManifest): number {
  return manifest.entries.reduce((sum, e) => sum + e.size, 0);
}

/**
 * One blob actually present in storage: its owning title id and current size.
 * This is "reality" — what {@link SaveDataStorage.list} found on disk — as
 * opposed to the manifest, which is the accounting we PERSISTED last time.
 */
export interface PresentBlob {
  readonly titleId: string;
  readonly size: number;
}

/**
 * Reconcile a (possibly stale / persisted) manifest against the blobs ACTUALLY
 * in storage, returning a fresh manifest that is the source of truth:
 *   - a title with a present blob keeps its recorded `updatedAt` (so we don't
 *     lose the write time) but takes the blob's CURRENT size (storage wins on
 *     size — the bytes are reality);
 *   - a title present in storage but ABSENT from the manifest is added with
 *     `updatedAt` 0 (we know it has a save, just not when it was written);
 *   - a title in the manifest with NO present blob is DROPPED (the blob was
 *     deleted out from under us — e.g. the user cleared site data).
 * Pure; never mutates inputs. Output is in stable titleId order. This is the
 * "reconcile the manifest with reality on load" the module promises, made a
 * testable unit so the async load wiring stays trivial.
 */
export function reconcileManifest(
  manifest: SaveDataManifest,
  present: readonly PresentBlob[],
): SaveDataManifest {
  const recorded = new Map<string, SaveDataEntry>();
  for (const e of manifest.entries) recorded.set(e.titleId, e);

  const byId = new Map<string, SaveDataEntry>();
  for (const blob of present) {
    const prior = recorded.get(blob.titleId);
    byId.set(blob.titleId, {
      titleId: blob.titleId,
      size: blob.size,
      updatedAt: prior ? prior.updatedAt : 0,
    });
  }
  const entries = Array.from(byId.values()).sort((a, b) =>
    a.titleId < b.titleId ? -1 : a.titleId > b.titleId ? 1 : 0,
  );
  return { entries };
}

/**
 * Serialise the manifest to a compact, deterministic JSON string. Entries are
 * written in titleId order so the output is stable and diff-friendly.
 */
export function serializeManifest(manifest: SaveDataManifest): string {
  const sorted = manifest.entries
    .slice()
    .sort((a, b) => (a.titleId < b.titleId ? -1 : a.titleId > b.titleId ? 1 : 0))
    .map((e) => ({ titleId: e.titleId, size: e.size, updatedAt: e.updatedAt }));
  return JSON.stringify({ entries: sorted });
}

/** Read one finite, non-negative integer field; fall back if absent / invalid. */
function readUint(obj: Record<string, unknown>, key: string, fallback: number): number {
  const v = obj[key];
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : fallback;
}

/** True iff `v` is a usable, non-empty save entry record we can trust. */
function isEntryRecord(v: unknown): v is Record<string, unknown> {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    typeof (v as Record<string, unknown>).titleId === "string" &&
    ((v as Record<string, unknown>).titleId as string).length > 0
  );
}

/**
 * Parse a persisted string back into a clean {@link SaveDataManifest}. TOTAL —
 * it never throws:
 *   - invalid JSON / null / non-object / missing `entries` -> empty manifest;
 *   - non-array `entries` -> empty manifest;
 *   - entries that are not objects or lack a string `titleId` -> skipped;
 *   - missing / wrong-typed `size` / `updatedAt` -> 0;
 *   - duplicate titleIds -> the LAST one wins (a self-healing dedupe).
 * So a corrupt or stale store yields a clean manifest rather than crashing.
 */
export function deserializeManifest(raw: string | null | undefined): SaveDataManifest {
  if (raw === null || raw === undefined) return emptyManifest();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyManifest();
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return emptyManifest();
  }
  const rawEntries = (parsed as Record<string, unknown>).entries;
  if (!Array.isArray(rawEntries)) return emptyManifest();

  // Dedupe by titleId, last-wins, then emit in stable id order.
  const byId = new Map<string, SaveDataEntry>();
  for (const candidate of rawEntries) {
    if (!isEntryRecord(candidate)) continue;
    const titleId = candidate.titleId as string;
    byId.set(titleId, {
      titleId,
      size: readUint(candidate, "size", 0),
      updatedAt: readUint(candidate, "updatedAt", 0),
    });
  }
  const entries = Array.from(byId.values()).sort((a, b) =>
    a.titleId < b.titleId ? -1 : a.titleId > b.titleId ? 1 : 0,
  );
  return { entries };
}

/**
 * The blob persistence seam. Async (OPFS is async). Tiny on purpose: read / write
 * / delete an opaque per-title byte buffer, plus list the title ids that have a
 * stored blob (so the manifest can be reconciled with reality on load).
 *
 * The model functions above never call these; this is the wiring the app uses to
 * load a title's save before a run and to persist it after.
 */
export interface SaveDataStorage {
  /** The bytes for `titleId`, or null if none are stored. */
  read(titleId: string): Promise<Uint8Array | null>;
  /** Persist (overwrite) the bytes for `titleId`. */
  write(titleId: string, bytes: Uint8Array): Promise<void>;
  /** Delete any stored bytes for `titleId` (no-op if none). */
  delete(titleId: string): Promise<void>;
  /** The title ids that currently have a stored blob. */
  list(): Promise<readonly string[]>;
}

/** Defensive copy so the fake can't be mutated through a shared reference. */
function copyBytes(bytes: Uint8Array): Uint8Array {
  return bytes.slice();
}

/**
 * The in-memory storage fake: the default adapter and the one tests drive, so the
 * save-data logic is fully verifiable without a browser. Bytes are copied in and
 * out so callers can't mutate stored buffers by reference. A real OPFS adapter
 * implements the same {@link SaveDataStorage} interface behind this seam.
 */
export function createMemorySaveDataStorage(): SaveDataStorage {
  const blobs = new Map<string, Uint8Array>();
  return {
    read(titleId: string): Promise<Uint8Array | null> {
      const b = blobs.get(titleId);
      return Promise.resolve(b ? copyBytes(b) : null);
    },
    write(titleId: string, bytes: Uint8Array): Promise<void> {
      blobs.set(titleId, copyBytes(bytes));
      return Promise.resolve();
    },
    delete(titleId: string): Promise<void> {
      blobs.delete(titleId);
      return Promise.resolve();
    },
    list(): Promise<readonly string[]> {
      return Promise.resolve(Array.from(blobs.keys()));
    },
  };
}

/**
 * Build the live, reconciled manifest from a storage adapter plus the last
 * PERSISTED manifest string (the accounting we wrote last session, e.g. from
 * localStorage — pass null/undefined if none). Lists every blob actually in
 * storage, reads each one's byte length, then {@link reconcileManifest}s that
 * reality against the persisted accounting so the result is authoritative:
 * storage wins on existence and size; the persisted record contributes only the
 * `updatedAt` of blobs that still exist. Degrades gracefully — a blob that
 * vanishes between list() and read() is simply skipped.
 *
 * This is the async wiring the app calls on boot; all the LOGIC it relies on is
 * the pure, unit-tested {@link reconcileManifest}, so this stays thin and is
 * itself exercised through the in-memory fake.
 */
export async function loadManifest(
  storage: SaveDataStorage,
  persisted: string | null | undefined,
): Promise<SaveDataManifest> {
  const ids = await storage.list();
  const present: PresentBlob[] = [];
  for (const titleId of ids) {
    const bytes = await storage.read(titleId);
    if (bytes === null) continue; // raced/removed between list and read.
    present.push({ titleId, size: bytes.byteLength });
  }
  return reconcileManifest(deserializeManifest(persisted), present);
}

/**
 * Persist (or refresh) one title's save blob END TO END: write the opaque bytes
 * to the blob {@link SaveDataStorage} AND record the accounting in the manifest,
 * returning the fresh manifest. This is the write half of the round-trip the app
 * calls when a run ends with new save data: storage gets the bytes, the manifest
 * gets size + write time (so the Detail screen and the next load reflect it).
 *
 * Pure orchestration over the tested {@link recordSave} + the storage seam; never
 * mutates the input manifest. §1.6: `bytes` are the game's OWN runtime save data
 * — an opaque buffer we move and account for, never decrypted content.
 *
 * @param now epoch ms to stamp the write with (injectable for tests).
 */
export async function persistSave(
  storage: SaveDataStorage,
  manifest: SaveDataManifest,
  titleId: string,
  bytes: Uint8Array,
  now = Date.now(),
): Promise<SaveDataManifest> {
  await storage.write(titleId, bytes);
  return recordSave(manifest, titleId, bytes.byteLength, now);
}

/**
 * Remove one title's save END TO END: delete its blob from the
 * {@link SaveDataStorage} AND forget its manifest accounting, returning the fresh
 * manifest. The delete-half of the round-trip the app calls when the user clears
 * a title's save. Pure orchestration over the tested {@link forgetSave} + the
 * storage seam; deleting a save that isn't there is harmless (storage delete is a
 * no-op, forgetSave returns the same reference).
 */
export async function removeSave(
  storage: SaveDataStorage,
  manifest: SaveDataManifest,
  titleId: string,
): Promise<SaveDataManifest> {
  await storage.delete(titleId);
  return forgetSave(manifest, titleId);
}

/**
 * A minimal key/value surface (Window.localStorage in the browser), mirroring the
 * settings model's {@link KeyValueStore}. The manifest accounting (which titles
 * have a save, plus size + write time) is small JSON kept here; the blobs
 * themselves live in OPFS behind {@link SaveDataStorage}.
 */
export interface ManifestKeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The localStorage key the manifest persistence seam reads/writes under. */
export const SAVEDATA_MANIFEST_KEY = "voland.savedata.manifest";

/**
 * The persistence seam for the manifest ACCOUNTING (not the blobs). Tiny on
 * purpose, mirroring {@link SettingsStorage}: load the persisted string (for
 * reconciliation on boot) and save the current manifest. The blobs round-trip
 * through {@link SaveDataStorage}; this is only the small JSON record of them.
 */
export interface ManifestStore {
  /** The last persisted manifest JSON, or null if nothing has been saved. */
  load(): string | null;
  /** Persist the manifest (serialised). */
  save(manifest: SaveDataManifest): void;
}

/** In-memory manifest store: the default + the one tests drive. Round-trips
 *  through serialize/deserialize so it exercises the same path the real adapter does. */
export function createMemoryManifestStore(seed?: SaveDataManifest): ManifestStore {
  let raw: string | null = seed ? serializeManifest(seed) : null;
  return {
    load(): string | null {
      return raw;
    },
    save(manifest: SaveDataManifest): void {
      raw = serializeManifest(manifest);
    },
  };
}

/**
 * The real manifest persistence adapter over a {@link ManifestKeyValueStore}
 * (Window.localStorage in the browser). Build-verified; it shares the SAME tested
 * serialize/deserialize as everything else, so its behaviour is covered by those
 * tests. All access is wrapped so a throwing/blocked store (private mode, quota)
 * degrades to a no-op rather than crashing.
 */
export function createLocalStorageManifest(
  store: ManifestKeyValueStore,
  key: string = SAVEDATA_MANIFEST_KEY,
): ManifestStore {
  return {
    load(): string | null {
      try {
        return store.getItem(key);
      } catch {
        return null;
      }
    },
    save(manifest: SaveDataManifest): void {
      try {
        store.setItem(key, serializeManifest(manifest));
      } catch {
        /* storage unavailable (private mode / quota) — drop silently. */
      }
    },
  };
}

/**
 * The OPFS directory subtree we keep save blobs under, and the file extension
 * for each title's blob. One flat directory of `<encoded-titleId>.sav` files.
 */
export const SAVEDATA_DIR = "voland-savedata";
const SAVEDATA_EXT = ".sav";

/** A title id may contain characters illegal in OPFS file names (":" etc.), so
 *  blobs are stored under the percent-encoded id. Reversible and collision-free. */
function fileNameFor(titleId: string): string {
  return `${encodeURIComponent(titleId)}${SAVEDATA_EXT}`;
}

/** Recover a title id from a stored blob's file name (inverse of fileNameFor). */
function titleIdFromFileName(name: string): string | null {
  if (!name.endsWith(SAVEDATA_EXT)) return null;
  const encoded = name.slice(0, -SAVEDATA_EXT.length);
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

/**
 * The real persistence adapter over the Origin Private File System (OPFS). Stores
 * each title's save blob as one file in a dedicated subdirectory of the origin's
 * private root. Browser-only and build-verified; its behaviour mirrors the tested
 * in-memory fake (same interface, same copy semantics). All access is wrapped so
 * a missing file / unavailable OPFS degrades to "no save" rather than crashing.
 *
 * `getRoot` is injected (defaults to `navigator.storage.getDirectory()`) so the
 * adapter is constructible without touching globals at import time.
 */
export function createOpfsSaveDataStorage(
  getRoot: () => Promise<FileSystemDirectoryHandle> = () => navigator.storage.getDirectory(),
): SaveDataStorage {
  async function dir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
    try {
      const root = await getRoot();
      return await root.getDirectoryHandle(SAVEDATA_DIR, { create });
    } catch {
      return null;
    }
  }

  return {
    async read(titleId: string): Promise<Uint8Array | null> {
      try {
        const d = await dir(false);
        if (!d) return null;
        const handle = await d.getFileHandle(fileNameFor(titleId), { create: false });
        const file = await handle.getFile();
        const buf = await file.arrayBuffer();
        return new Uint8Array(buf);
      } catch {
        // Not found / unavailable -> treat as no save.
        return null;
      }
    },
    async write(titleId: string, bytes: Uint8Array): Promise<void> {
      const d = await dir(true);
      if (!d) return; // OPFS unavailable -> drop silently (mirrors settings adapter).
      try {
        const handle = await d.getFileHandle(fileNameFor(titleId), { create: true });
        const writable = await handle.createWritable();
        // Slice to a tight ArrayBuffer view so we never write trailing bytes from
        // a larger backing buffer.
        await writable.write(bytes.slice());
        await writable.close();
      } catch {
        /* quota / blocked — drop silently. */
      }
    },
    async delete(titleId: string): Promise<void> {
      try {
        const d = await dir(false);
        if (!d) return;
        await d.removeEntry(fileNameFor(titleId));
      } catch {
        /* not found / unavailable -> nothing to delete. */
      }
    },
    async list(): Promise<readonly string[]> {
      const out: string[] = [];
      try {
        const d = await dir(false);
        if (!d) return out;
        // OPFS directory handles are async-iterable over [name, handle] pairs.
        const entries = (d as unknown as {
          entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
        }).entries();
        for await (const [name] of entries) {
          const id = titleIdFromFileName(name);
          if (id !== null) out.push(id);
        }
      } catch {
        /* unavailable -> empty. */
      }
      return out;
    },
  };
}
